import { resolveMoneyDecimals, roundMoney, sumMoney } from "./money";

/**
 * El impuesto al consumo de bolsas de plástico (ICBPER, Perú).
 *
 * Un producto con `Plastic_Bag` (el `Plactic_Bag` de
 * `importaciones_getproductostock`) lleva, por cada unidad vendida, un importe
 * fijo que publica el OData `ConfContabilidad` (`Unit_Tax_Plastic_Bag`).
 *
 * La línea del impuesto **no se guarda ni viaja a Business Central**: BC lo
 * calcula por su cuenta al recibir la línea del producto. Acá se deriva de la
 * línea del producto --que guarda el importe unitario y la cuenta vigentes al
 * agregarla-- para mostrarla en la venta y en el ticket, y su importe sí entra
 * en el total a cobrar: si no, el pago no cuadraría con el total de BC.
 *
 * Al derivarse, sigue sola a la línea del producto: cambiar la cantidad la
 * actualiza, borrar el producto la borra, y nunca puede quedar huérfana.
 */

/** ¿Esta línea de producto genera la línea del impuesto? */
export const llevaImpuestoDeBolsa = (line) =>
  Boolean(line) &&
  !line._isDeleted &&
  line.Plastic_Bag === true &&
  Number(line.Unit_Tax_Plastic_Bag) > 0;

/**
 * ¿Esta línea es una bolsa?
 *
 * Una bolsa no se devuelve: no entra en las notas de crédito, ni cuenta como
 * pendiente de abonar. La marca de la línea no alcanza para saberlo: sólo la
 * lleva si al venderla había impuesto configurado, y las líneas traídas de
 * Business Central no la traen nunca. Por eso también se mira el producto.
 *
 * @param {object} line
 * @param {Set<string>} [numerosDeBolsas] Los `No` de los productos con `Plastic_Bag`.
 */
export const esLineaDeBolsa = (line, numerosDeBolsas) =>
  Boolean(line) && (line.Plastic_Bag === true || Boolean(numerosDeBolsas?.has(line.No)));

/** Las líneas que se pueden abonar: todas menos las bolsas. */
export const sinLineasDeBolsa = (lines, numerosDeBolsas) =>
  (lines || []).filter((line) => !esLineaDeBolsa(line, numerosDeBolsas));

/** El importe del impuesto de una línea de producto (con el signo de su cantidad). */
export const importeDeImpuestoDeBolsa = (line, country = null) => {
  if (!llevaImpuestoDeBolsa(line)) return 0;
  const decimals = resolveMoneyDecimals(country);
  return roundMoney((Number(line.Quantity) || 0) * Number(line.Unit_Tax_Plastic_Bag), decimals);
};

/** El impuesto de todas las líneas del documento. */
export const totalDeImpuestoDeBolsas = (lines = [], country = null) => {
  const decimals = resolveMoneyDecimals(country);
  return sumMoney(
    (lines || []).filter(llevaImpuestoDeBolsa),
    (line) => importeDeImpuestoDeBolsa(line, country),
    decimals
  );
};

/**
 * La línea visual del impuesto para una línea de producto, o `null`.
 *
 * Lleva `esImpuestoDeBolsa` para que quien la pinte no le ofrezca editar,
 * descontar ni borrar: no es una línea del documento.
 */
export const lineaDeImpuestoDeBolsa = (line, country = null) => {
  if (!llevaImpuestoDeBolsa(line)) return null;

  return {
    key: `${line.key ?? line.id ?? line.No}-impuesto-bolsa`,
    esImpuestoDeBolsa: true,
    lineaDelProducto: line.key ?? line.id,
    No: line.Account_Tax_Bag || "",
    Description: line.Name_Account_Tax_Bag || "IMPUESTO AL CONSUMO DE BOLSAS DE PLASTICO",
    Quantity: Number(line.Quantity) || 0,
    Unit_Price: Number(line.Unit_Tax_Plastic_Bag),
    importe: importeDeImpuestoDeBolsa(line, country),
  };
};

/**
 * Las líneas tal como se muestran: cada producto con bolsa seguido de su línea
 * de impuesto.
 */
export const conLineasDeImpuestoDeBolsa = (lines = [], country = null) =>
  (lines || []).flatMap((line) => {
    const impuesto = lineaDeImpuestoDeBolsa(line, country);
    return impuesto ? [line, impuesto] : [line];
  });
